// components/appointments/AvailabilityWarning.js
import React from 'react';
import { AlertTriangle } from 'lucide-react';
import { useTranslations } from '../../hooks/useTranslations';

export function AvailabilityWarning({ 
  crewMemberName, 
  conflictInfo, 
  appointmentDate, 
  appointmentTime 
}) {
  const t = useTranslations();

  if (!conflictInfo || !conflictInfo.hasConflict) {
    return null;
  }

  // Format day name for display using translations
  const formatDayName = (day) => {
    const dayTranslations = {
      monday: t.monday,
      tuesday: t.tuesday,
      wednesday: t.wednesday, 
      thursday: t.thursday,
      friday: t.friday,
      saturday: t.saturday,
      sunday: t.sunday
    };
    return dayTranslations[day] || day;
  };

  // Format date for display (Hebrew locale)
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const { dayName, isWorkingDay, availableRanges, conflictType, workFrequencyWeeks, workReferenceDate } = conflictInfo;

  // Handle work frequency conflicts
  if (conflictType === 'work_frequency') {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <AlertTriangle className="w-5 h-5 ml-3 text-red-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-red-800 mb-2">
              {t.workFrequencyConflict}
            </h4>
            
            <p className="text-sm text-red-700 mb-3">
              <strong>{crewMemberName}</strong> {t.worksEvery} <strong>{workFrequencyWeeks}</strong> {t.weeksOnly}
              {workReferenceDate && (
                <>
                  {' '}{t.startingFrom} <strong>{formatDate(workReferenceDate)}</strong>
                </>
              )}
              .
            </p>

            <p className="text-sm text-red-700 mb-3">
              {t.selectedDateNotInWorkingWeek} <strong>{formatDate(appointmentDate)}</strong>.
            </p>

            <p className="text-xs text-red-600 mt-2 italic">
              {t.canStillCreateAppointmentNote}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
      <div className="flex items-start space-x-3">
        <AlertTriangle className="w-5 h-5 ml-3 text-red-500 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-medium text-red-800 mb-2">
            {t.availabilityConflict}
          </h4>
          
          <p className="text-sm text-red-700 mb-3">
            <strong>{crewMemberName}</strong> {t.notAvailableAt}{' '}
            <strong>{appointmentTime}</strong> {t.on}{' '}
            <strong>{formatDate(appointmentDate)}</strong>.
          </p>

          {isWorkingDay && availableRanges.length > 0 ? (
            <div className="text-sm text-red-700">
              <p className="mb-1">
                <strong>{crewMemberName}</strong> {t.isAvailableOn}{formatDayName(dayName)} {t.during}:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-1">
                {availableRanges.map((range, index) => (
                  <li key={index}>
                    {range.end} - {range.start}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-sm text-red-700">
              <strong>{crewMemberName}</strong> {t.doesNotWorkOn}{formatDayName(dayName)}.
            </p>
          )}

          <p className="text-xs text-red-600 mt-2 italic">
            {t.canStillCreateAppointmentNote}
          </p>
        </div>
      </div>
    </div>
  );
}